/**
 * The rebuilt pre-send payload block — moved verbatim out of
 * `../DispatchInspector.tsx` (plan 2026-09-27-003, U6). Since the redesign it
 * backs the member side panel's "Messages for <name>"; a sent row's copy is
 * "What was sent" (`SentCopyPanel.tsx`).
 */
import type { UseQueryResult } from '@tanstack/react-query';
import { Alert } from '@clay-run/glaze/alert';
import { LoadingSpinner } from '@clay-run/glaze/loading-spinner';
import { StatusBadge } from '@clay-run/glaze/status-badge';
import { Text } from '@clay-run/glaze/text';

import {
  dataWarningBadgeText,
  extractOverrideSteps,
  gongRunsStepCaption,
  replySubjectOmittedCaption,
  stepResolutionsByOrdinal,
  visibleOverrideSteps,
} from '../../../dispatch-inspector-logic';
import type { PlaybooksDispatchPayloadResponse } from '../../../schemas';

// The rebuilt payload: a collapsible raw-JSON block plus a rendered
// `overrideSteps` list. HTML is shown as TEXT everywhere here — never
// injected (`dangerouslySetInnerHTML`) — this is a read-only debug/audit
// view, not an editor.

export function PayloadBlock({
  query,
  showRawJson = true,
  hosted = false,
  hidePaddingSteps = false,
}: {
  query: Pick<UseQueryResult<PlaybooksDispatchPayloadResponse>, 'isLoading' | 'isError' | 'data'>;
  /** The collapsible raw-JSON block. The Dispatch log keeps it; a rep-facing host (the member panel) hides it so ids never show. */
  showRawJson?: boolean;
  /** Inside a host that already draws its own border: drops this block's own top rule so the two do not double up. */
  hosted?: boolean;
  /** Hides the Clay package's trailing blank filler slots (the member panel). The Dispatch log keeps every slot: it is an audit view. */
  hidePaddingSteps?: boolean;
}) {
  const rule = hosted ? '' : 'border-t border-border-secondary ';
  if (query.isLoading) {
    return (
      <div className={`flex justify-center ${rule}p-3`}>
        <LoadingSpinner size="sm" />
      </div>
    );
  }
  if (query.isError) {
    return (
      <div className={`${rule}p-3`}>
        <Alert status="error">Couldn&apos;t load this payload.</Alert>
      </div>
    );
  }
  if (!query.data) return null;
  // Captured into a local so the narrowing above survives into the `.map`
  // closure below — TS does not carry a `!query.data` narrowing of a
  // parameter's property through a nested function expression.
  const data = query.data;

  const allSteps = extractOverrideSteps(data.payload);
  const steps = hidePaddingSteps ? visibleOverrideSteps(allSteps, data.resolution) : allSteps;
  // `resolution.steps` only covers AUTHORED steps (padding steps carry no
  // entry). Its `position` field is the step's own AUTHORED slot — NOT the
  // array index — and the two only coincide when every authored position
  // happens to be contiguous from 0. A rep deleting or reordering a step
  // leaves gaps (e.g. authored positions `[0, 2]`), so this preview's rows
  // (and `replySubjectOmittedCaption` below) are keyed by ORDINAL — each
  // entry's own index in `resolution.steps`, matching `overrideSteps`'s
  // index — via `stepResolutionsByOrdinal` (`dispatch-inspector-logic.ts`).
  // Keying by `position` instead mislabels the preview whenever a gap
  // exists: it was found attaching a step's unknown-token badge to a blank
  // padding row instead of the step that actually has the typo.
  // QA round 7, U5 (item 7) — this `index + 1` numbering is ALREADY the
  // shared convention: `steps` here is `overrideSteps`, already built in
  // send order, so its array index is precisely what `gong-flow-picker-api.ts`'s
  // `stepOrdinal` computes for the authoring surfaces (the 1-based index in
  // the position-sorted list). Nothing to change here — this is the numbering
  // every other surface was made to match.
  const resolutionByOrdinal = stepResolutionsByOrdinal(data.resolution.steps);

  return (
    <div className={`flex flex-col gap-2 ${rule}p-3`}>
      <Text size="xs" variant="tertiary">
        {data.note}
      </Text>

      {steps.length > 0 && (
        <div className="flex flex-col gap-2">
          <Text size="xs" weight="medium" variant="tertiary">
            Override steps
          </Text>
          <ol className="flex flex-col gap-2">
            {steps.map((step, index) => {
              const stepResolution = resolutionByOrdinal.get(index);
              const replyOmittedCaption = replySubjectOmittedCaption(
                index + 1,
                data.resolution.replySubjectsOmitted,
              );
              // A truly blank Gong-run non-email slot (Call / LinkedIn /
              // Custom) is Gong's own task, not a missing-copy defect — show
              // its action-type caption instead of the "(no subject)"/
              // "(no body)" placeholders that would otherwise read as an
              // authoring mistake (playbooks-gong-nonemail-steps-fix, U4).
              const gongCaption = gongRunsStepCaption(step);
              return (
                <li key={index} className="rounded border border-border-secondary p-2">
                  <Text size="xs" weight="medium">
                    {`${index + 1}. ${step.subject || (gongCaption === null ? '(no subject)' : '')}`}
                  </Text>
                  {replyOmittedCaption !== null && (
                    <Text size="xs" variant="tertiary">
                      {replyOmittedCaption}
                    </Text>
                  )}
                  {gongCaption !== null ? (
                    <Text size="xs" variant="tertiary">
                      {gongCaption}
                    </Text>
                  ) : (
                    <Text size="xs" variant="tertiary" className="whitespace-pre-wrap">
                      {step.body || '(no body)'}
                    </Text>
                  )}
                  {stepResolution &&
                    (stepResolution.empty.length > 0
                      || stepResolution.unknown.length > 0
                      || stepResolution.valueDefects.length > 0) && (
                      <div className="mt-1 flex flex-wrap items-center gap-1">
                        {/* U0f — a placeholder in the record is its own warning, not "empty". */}
                        {stepResolution.valueDefects.map((defect) => (
                          <StatusBadge key={`value-${defect.token}`} status="warning">
                            {dataWarningBadgeText(defect)}
                          </StatusBadge>
                        ))}
                        {stepResolution.empty
                          .filter((token) => !stepResolution.valueDefects.some((defect) => defect.token === token))
                          .map((token) => (
                            <StatusBadge key={`empty-${token}`} status="warning">
                              {`{{${token}}} empty`}
                            </StatusBadge>
                          ))}
                        {stepResolution.unknown.map((token) => (
                          <StatusBadge key={`unknown-${token}`} status="error">
                            {`{{${token}}} unknown`}
                          </StatusBadge>
                        ))}
                      </div>
                    )}
                </li>
              );
            })}
          </ol>
        </div>
      )}

      {showRawJson && (
        <details>
          <summary className="cursor-pointer text-xs text-content-tertiary">Raw payload JSON</summary>
          <div className="mt-1 overflow-x-auto rounded bg-bg-secondary p-2">
            <pre className="text-xs">{JSON.stringify(query.data.payload, null, 2)}</pre>
          </div>
        </details>
      )}
    </div>
  );
}
